'use client';

/**
 * Página de RENDER del libro — la fotografía Chrome headless, no la ve un humano.
 *
 * Pinta cada carilla del Programa de Semana Santa **con los mismos componentes
 * que el libro de la web** (`buildPages` + `HalfPageSlot` de `ProgramaSSBook`),
 * una debajo de otra, en tamaño fijo y sin animaciones. La Cloud Function
 * `renderProgramaSS` abre esta ruta, espera a `#render-ready` y hace una captura
 * por cada `[data-carilla]`; esos PNG son los que enseña el librito dentro de
 * Minecraft.
 *
 * La consecuencia buena: **no hay una segunda maquetación**. Si cambias el libro
 * de la web, cambia el del juego, porque es literalmente el mismo componente.
 *
 * Parámetros (querystring):
 *   w, h   tamaño de la carilla en px CSS (por defecto 1200 × 1600, proporción 3:4)
 *
 * Es pública a propósito: el programa ya es público desde que está en la portada,
 * y así el renderizador no necesita sesión.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { getCityMapConfig, getDiasSS, getProgramaSSConfig } from '@/lib/actions';
import { sanitizeFirestoreData } from '@/lib/utils';
import { ordenarDiasSS } from '@/lib/programa-ss';
import { escalaDelMapa, longitudEnMetros } from '@/lib/programa-ss';
import { buildPages, CARILLA_ALTO, CARILLA_ANCHO, HalfPageSlot } from '@/components/programa-ss/ProgramaSSBook';
import type { Banda, DiaSS, JartinetHermandad, ProgramaSSConfig } from '@/lib/types';

// Tamaño de una carilla EN PANTALLA (la mitad de un pliego). No se sube para
// ganar resolución: las fuentes van en px y no crecerían con la caja, así que
// saldría la misma página con la letra pequeña. La resolución la pone el
// `deviceScaleFactor` del renderizador.
// La medida es la MISMA que usa el libro de la web (`CarillaFija` la escala para
// que quepa donde toque), así que la fotografía y la pantalla son idénticas.
const ANCHO_POR_DEFECTO = CARILLA_ANCHO;
const ALTO_POR_DEFECTO = CARILLA_ALTO;

export default function RenderProgramaSSPage() {
  const [params] = useSearchParams();
  const ancho = Number(params.get('w')) || ANCHO_POR_DEFECTO;
  const alto = Number(params.get('h')) || ALTO_POR_DEFECTO;

  const [days, setDays] = useState<DiaSS[]>([]);
  const [config, setConfig] = useState<ProgramaSSConfig | null>(null);
  const [cityMapUrl, setCityMapUrl] = useState<string | null>(null);
  const [hermandades, setHermandades] = useState<JartinetHermandad[]>([]);
  const [bandas, setBandas] = useState<Banda[]>([]);
  const [datosListos, setDatosListos] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [imagenesListas, setImagenesListas] = useState(false);

  // Mismos orígenes que la sección pública de la portada: todo lectura abierta.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const [diasResult, configResult, cityMapResult, hermandadesSnap, bandasSnap] = await Promise.all([
        getDiasSS(),
        getProgramaSSConfig(),
        getCityMapConfig(),
        getDocs(collection(db, 'jartinet_hermandades')),
        getDocs(collection(db, 'bandas')),
      ]);
      if (cancelado) return;
      setDays(ordenarDiasSS(diasResult.dias || []));
      setConfig(configResult.config);
      setCityMapUrl(cityMapResult.config?.mapImageUrl || null);
      setHermandades(hermandadesSnap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as JartinetHermandad[]);
      setBandas(bandasSnap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as Banda[]);
      setDatosListos(true);
    })().catch((e) => { if (!cancelado) setFallo(String(e?.message || e)); });
    return () => { cancelado = true; };
  }, []);

  // Con el tamaño de la carilla, `buildPages` APRIETA el cuadro de horarios en
  // vez de dejar que scrollee: en una foto lo que sobra por abajo se pierde
  // (los días de muchas horas salían cortados).
  const pages = useMemo(
    () => buildPages(days, cityMapUrl, { ancho, alto }),
    [days, cityMapUrl, ancho, alto],
  );
  const escala = useMemo(() => escalaDelMapa(config?.mapCalib), [config?.mapCalib]);
  const metrosCarreraOficial = useMemo(
    () => longitudEnMetros(config?.carreraOficial, escala),
    [config?.carreraOficial, escala],
  );

  /**
   * Espera a que TODAS las imágenes (escudos, portadas, nazarenos, mapa) estén
   * descargadas antes de dar por lista la página. Sin esto, las capturas salen
   * con huecos: Chrome fotografía en cuanto el DOM está montado y las fotos van
   * por su cuenta. Las que fallen también cuentan (`onerror`), o una foto rota
   * dejaría el render colgado para siempre.
   */
  useEffect(() => {
    if (!datosListos || pages.length === 0) return;
    let cancelado = false;

    const esperar = async () => {
      // Un par de fotogramas para que React haya pintado los <img> del árbol.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const imgs = Array.from(document.images);
      await Promise.all(
        imgs.map((img) => (img.complete ? Promise.resolve() : new Promise<void>((res) => {
          img.addEventListener('load', () => res(), { once: true });
          img.addEventListener('error', () => res(), { once: true });
        }))),
      );
      // Y las fuentes, o la primera carilla sale con la tipografía de respaldo.
      try { await (document as any).fonts?.ready; } catch { /* navegador sin la API */ }
      if (!cancelado) setImagenesListas(true);
    };

    esperar();
    return () => { cancelado = true; };
  }, [datosListos, pages.length]);

  if (fallo) {
    return <div id="render-error" style={{ padding: 24, color: '#f87171' }}>Error cargando el programa: {fallo}</div>;
  }

  return (
    <div style={{ background: '#000', padding: 0, margin: 0 }}>
      {/* Fuera los mandos que solo tienen sentido con un ratón delante. */}
      <style>{`[data-solo-pantalla] { display: none !important; }`}</style>

      {/* Marcadores que lee la Cloud Function */}
      {imagenesListas && <div id="render-ready" data-carillas={pages.length} style={{ height: 0, overflow: 'hidden' }} />}

      {pages.map((page, i) => (
        <div
          key={i}
          data-carilla={i}
          // `bg-card` + el padding del libro: cada carilla es autosuficiente, así
          // que dos seguidas reconstruyen el pliego tal cual se ve en la web.
          className="relative overflow-hidden bg-card"
          style={{ width: ancho, height: alto, padding: 24 }}
        >
          <HalfPageSlot
            page={page}
            allHermandades={hermandades}
            allBandas={bandas}
            cityMapUrl={cityMapUrl}
            carreraOficial={config?.carreraOficial || []}
            catedralPunto={config?.catedralPunto}
            escala={escala}
            metrosCarreraOficial={metrosCarreraOficial}
          />

          {/* Sombra del lomo: en el libro va entre las dos carillas, así que cae
              en el borde DERECHO de las pares (izquierda del pliego) y en el
              IZQUIERDO de las impares. Bakeada aquí para que el mod solo tenga
              que poner las dos imágenes juntas. */}
          <div
            className="pointer-events-none absolute inset-y-0 z-20"
            style={{
              width: 28,
              [i % 2 === 0 ? 'right' : 'left']: 0,
              background: `linear-gradient(to ${i % 2 === 0 ? 'left' : 'right'}, rgba(0,0,0,0.35), transparent)`,
            } as React.CSSProperties}
          />
        </div>
      ))}
    </div>
  );
}
